<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFollowUpStatus } from 'dashboard/composables/useFollowUpStatus';
import { toquesDoFollowUp } from './toquesDoFollowUp';
import followUpCadenceRunsAPI from 'dashboard/api/followUpCadenceRuns';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// Card na sidebar do contato — pedido do Victor (2026-07-22), par do selo
// no header (FollowUpHeaderBadge.vue). Mesma fonte de dados
// (useFollowUpStatus), só muda a apresentação (mais detalhe: nome da
// cadência + próxima ação).
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const { run, refetch } = useFollowUpStatus(() => props.conversationId);

// Pedido do Victor (2026-07-22): pausar o follow-up direto da sidebar, sem
// precisar abrir o painel de gestão separado. Reaproveita o mesmo
// pause_manually!/resume! (e a mesma API) que o painel já usa — só muda
// onde o botão mora. awaiting_approval fica de fora de propósito: pausar
// só faz sentido a partir de active (ver FollowUpCadenceRun#pause_manually!),
// e nesse estado quem decide é a revisão do canário, não esse toggle.
const isTogglable = computed(() =>
  ['active', 'paused_manual'].includes(run.value?.status)
);
const isToggling = ref(false);

const handleToggle = async newValue => {
  isToggling.value = true;
  try {
    if (newValue) {
      await followUpCadenceRunsAPI.resume(run.value.id);
    } else {
      await followUpCadenceRunsAPI.pause(run.value.id);
    }
    await refetch();
  } catch {
    useAlert(t('FOLLOW_UP_CADENCES.INDICATOR.TOGGLE_ERROR'));
  } finally {
    isToggling.value = false;
  }
};

// Mensagens enviadas, não o número do passo (ver toquesDoFollowUp).
const toques = computed(() => toquesDoFollowUp(run.value));
const progressPercent = computed(() => toques.value.porcento);

// Mesma lógica de tempo relativo do painel de gestão
// (FollowUpCadencesView.vue#formatRelative) — duplicada de propósito, é
// pequena o bastante e os dois lugares podem divergir de granularidade
// no futuro sem acoplar componentes de contextos diferentes.
const nextActionRelative = computed(() => {
  const ts = run.value?.next_action_at;
  if (!ts) return '';
  const diffMs = ts * 1000 - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const absMin = Math.abs(diffMin);

  if (absMin < 1) return t('FOLLOW_UP_CADENCES.PANEL.NOW');
  if (absMin < 60) {
    return diffMin > 0
      ? t('FOLLOW_UP_CADENCES.PANEL.IN_MINUTES', { count: absMin })
      : t('FOLLOW_UP_CADENCES.PANEL.OVERDUE_MINUTES', { count: absMin });
  }
  const absHours = Math.round(absMin / 60);
  if (absHours < 24) {
    return diffMin > 0
      ? t('FOLLOW_UP_CADENCES.PANEL.IN_HOURS', { count: absHours })
      : t('FOLLOW_UP_CADENCES.PANEL.OVERDUE_HOURS', { count: absHours });
  }
  const absDays = Math.round(absHours / 24);
  return diffMin > 0
    ? t('FOLLOW_UP_CADENCES.PANEL.IN_DAYS', { count: absDays })
    : t('FOLLOW_UP_CADENCES.PANEL.OVERDUE_DAYS', { count: absDays });
});

const goToPanel = () => {
  router.push({
    name: 'follow_up_cadences',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div v-if="run">
    <p
      class="text-[10px] uppercase tracking-wider text-n-slate-9 font-semibold mb-2 px-1"
    >
      {{ t('FOLLOW_UP_CADENCES.INDICATOR.LABEL') }}
    </p>
    <div
      class="w-full flex flex-col gap-1.5 rounded-lg border border-n-brand/30 bg-n-brand/5 hover:bg-n-brand/10 transition-colors px-3 py-2.5 cursor-pointer"
      @click="goToPanel"
    >
      <div
        class="flex items-center gap-1.5 text-xs font-semibold text-n-blue-11"
      >
        <span class="i-ph-repeat size-3.5 flex-shrink-0" />
        <span class="truncate flex-1">{{ run.definition_name }}</span>
        <span
          v-if="isTogglable"
          v-tooltip="
            run.status === 'paused_manual'
              ? t('FOLLOW_UP_CADENCES.PANEL.RESUME')
              : t('FOLLOW_UP_CADENCES.PANEL.PAUSE')
          "
          class="flex-shrink-0"
          @click.stop
        >
          <Switch
            :model-value="run.status === 'active'"
            :disabled="isToggling"
            @update:model-value="handleToggle"
          />
        </span>
      </div>
      <div
        class="flex items-center justify-between text-[11px] text-n-slate-11"
      >
        <span>
          {{
            t('FOLLOW_UP_CADENCES.INDICATOR.ENVIADAS', {
              current: toques.enviados,
              total: toques.total,
            })
          }}
        </span>
        <span
          v-if="run.status === 'paused_manual'"
          class="font-medium text-n-slate-9"
        >
          {{ t('FOLLOW_UP_CADENCES.RUN_STATUS.PAUSED_MANUAL') }}
        </span>
        <span v-else-if="run.status === 'awaiting_approval'">
          {{ t('FOLLOW_UP_CADENCES.RUN_STATUS.AWAITING_APPROVAL') }}
        </span>
        <span v-else-if="nextActionRelative">
          {{
            t('FOLLOW_UP_CADENCES.INDICATOR.NEXT_ACTION', {
              time: nextActionRelative,
            })
          }}
        </span>
      </div>
      <!-- A barra da casa (24/10/2026): cresce ao abrir a conversa e desliza quando
           sai mais um toque, em vez de pular de largura. -->
      <span class="advup-barra__trilho advup-tom--azul" aria-hidden="true">
        <span class="advup-barra__fio" :style="{ '--pct': progressPercent }" />
      </span>
    </div>
  </div>
</template>
